import React, { Fragment, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Breadcrumbs as ReactBreadcrumbsDynamic } from 'react-breadcrumbs-dynamic';
import Breadcrumbs, { Crumb } from '../../regions/Breadcrumbs';
import ContactLinks from '../../regions/ContactLinks';
import Notice from '../../misc/Notice';
import Container from '../../layout/Container';
import CookieBanner from '../../regions/CookieBanner';
import SiteSearch from '../../regions/SiteSearch';
import Navigation from '../../regions/Navigation';
import UtilityButtons from '../../regions/UtilityButtons';
import UtilityLinks from '../../regions/UtilityLinks';
import LogoAuthentication from '../../regions/LogoAuthentication';
import Affiliations from '../../regions/Affiliations';
import SessionExtendModal from '../../modals/SessionExtendModal';
import useLanguageSwitcher from '../../../hooks/useLanguageSwitcher';
import useAuthentication from '../../../hooks/useAuthentication';

function BaseApp({ config, staticBreadcrumb, hasAuthentication = true, children }) {
  const { t, i18n } = useTranslation();

  const { isAuthenticated, account, login, logout, renewToken } = useAuthentication();
  const { languageSwitcherHref } = useLanguageSwitcher();

  const [settings, setSettings] = useState({
    menus: { main: [], header: [], campaign: [], footer: [] },
    settings: {
      cookie_url: '',
      feedback_email: '',
      telephone_text: '',
      telephone_number: '',
      contact_email: '',
      twitter_url: '',
      facebook_url: '',
      linkedin_url: '',
      youtube_url: '',
      instagram_url: ''
    }
  });

  useEffect(() => {
    // Get the settings from Drupal site.
    fetch(config.endpoints.settings[i18n.language])
      .then((response) => response.json())
      .then((response) => setSettings(response));
  }, []);

  return (
    <>
      <header>
        <a href="#main-content" className="visually-hidden focusable ignore-link">
          {t('styleguide:skip_to_main_content')}
        </a>
        <CookieBanner
          domain={config.domain}
          baseUrl={config.baseUrl[i18n.language]}
          settingsHref={
            settings.settings.cookie_url || config.baseUrl[i18n.language] + t('styleguide:urls.cookieSettings')
          }
          analytics={config.googleTagManager.analytics}
          marketing={config.googleTagManager.marketing}
          personalisation={config.googleTagManager.personalisation}
          webchat={config.googleTagManager.webchat}
        />

        <SiteSearch
          logoHref={i18n.language === 'cy' ? 'https://llyw.cymru/' : 'https://gov.wales/'}
          source={config.styleguide + '/assets/img/logos/wg-logo-light.svg'}
          languageHref={languageSwitcherHref}
          searchFormAction={config.baseUrl[i18n.language] + '/search/site'}
        />
        <LogoAuthentication
          domain={config.domain}
          langcode={i18n.language}
          source={
            config.styleguide +
            '/assets/img' +
            config.themePath +
            '/logos/' +
            config.domain +
            '-logo-' +
            i18n.language +
            '.svg'
          }
          sourceSmall={
            config.styleguide +
            '/assets/img' +
            config.themePath +
            '/logos/' +
            config.domain +
            '-logo-mob-' +
            i18n.language +
            '.svg'
          }
          username={account()?.name}
          profileHref={config.auth.profile[i18n.language]}
          helperLinks={settings.menus.header.map((item) => ({
            href: item.url,
            text: item.title
          }))}
          hasAuthentication={hasAuthentication}
          isAuthenticated={isAuthenticated()}
          onLogin={login}
          onLogout={logout}
        />
        <Navigation
          hasAuthentication={hasAuthentication}
          isAuthenticated={isAuthenticated()}
          name={account()?.name}
          profileHref={config.auth.profile[i18n.language]}
          menuLinks={settings.menus.main.map((item) => ({
            href: item.url,
            text: item.title
          }))}
          helperLinks={settings.menus.header.map((item) => ({
            href: item.url,
            text: item.title
          }))}
          onLogin={login}
          onLogout={logout}
        />
      </header>
      <main className="gel-content-main">
        <Container>
          <a id="main-content" tabIndex={-1} />
          <Breadcrumbs homeHref={config.baseUrl[i18n.language]}>
            {staticBreadcrumb && <Crumb href={staticBreadcrumb.href} text={staticBreadcrumb.text} />}
            <ReactBreadcrumbsDynamic
              item={Crumb}
              finalItem={Crumb}
              container={Fragment}
              finalProps={{
                isCurrent: true
              }}
              renameProps={{
                to: 'href',
                children: 'text',
                onClick: 'onPress'
              }}
            />
          </Breadcrumbs>
        </Container>
        {account()?.idTokenClaims?.exp && (
          <SessionExtendModal
            expires={account().idTokenClaims.exp}
            onExpire={logout}
            onExtension={() => renewToken()}
          />
        )}
        {children}
      </main>
      <footer>
        <ContactLinks
          supportText={settings.settings.telephone_text}
          phoneNumber={settings.settings.telephone_number}
          contactEmailAddress={settings.settings.contact_email}
          webchatHref={
            config.baseUrl[i18n.language] +
            t('styleguide:urls.contactUs') +
            '#' +
            t('styleguide:layout.footer.webchat.anchor')
          }
          bslHref={config.baseUrl[i18n.language] + '/' + t('styleguide:layout.footer.bsl.url')}
          socialMediaText={t('styleguide:join_us')}
          xHref={settings.settings.twitter_url}
          facebookHref={settings.settings.facebook_url}
          linkedInHref={settings.settings.linkedin_url}
          youTubeHref={settings.settings.youtube_url}
          instagramHref={settings.settings.instagram_url}
          feedbackEmailAddress={
            settings.settings.feedback_email +
            '?subject=' +
            t('styleguide:feedback_on') +
            ' ' +
            encodeURIComponent(window.location.href) +
            '&body=' +
            t('styleguide:page_url') +
            ': ' +
            encodeURIComponent(window.location.href) +
            '%0D%0A' +
            t('styleguide:page_title') +
            ': ' +
            encodeURIComponent(window.document.title) +
            '%0D%0A' +
            t('styleguide:your_feedback') +
            ':'
          }
          onOpenWebchat={(e) => {
            if (typeof window.LC_API !== 'undefined') {
              e.preventDefault();
              window.LC_API.open_chat_window();
            }
          }}
        />
        <UtilityButtons
          xHref={'https://x.com/share?url=' + window.location.href}
          facebookHref={'https://www.facebook.com/sharer/sharer.php?u=' + window.location.href}
          shareEmailAddress={'mailto:' + '?body=' + window.location.href + '&subject=' + t('styleguide:shared_from')}
          backToTopHref="#main-content"
        />
        <UtilityLinks
          campaignLinks={settings.menus.campaign.map((item) => ({
            href: item.url,
            text: item.title
          }))}
          legalLinks={settings.menus.footer.map((item) => ({
            href: item.url,
            text: item.title
          }))}
        />
        <Affiliations
          domain={config.domain}
          logos={[
            {
              href: i18n.language === 'cy' ? 'https://llyw.cymru/' : 'https://gov.wales/',
              title: t('styleguide:welsh_government'),
              source: config.styleguide + '/assets/img/logos/wg-logo.svg',
              alt: t('styleguide:wgheader_logo_alt')
            },
            config.domain === 'cw'
              ? {
                  href: i18n.language === 'cy' ? config.ww.cy : config.ww.en,
                  title: t('styleguide:working_wales'),
                  source:
                    i18n.language === 'cy'
                      ? config.styleguide + '/assets/img/logos/ww-cy.svg'
                      : config.styleguide + '/assets/img/logos/ww-en.svg',
                  alt: t('styleguide:ww_logo_alt')
                }
              : null,
            config.domain === 'ww'
              ? {
                  href: i18n.language === 'cy' ? config.cw.cy : config.cw.en,
                  title: t('styleguide:careers_wales'),
                  source: config.styleguide + '/assets/img/logos/cw-logo.svg',
                  alt: t('styleguide:cw_logo_alt')
                }
              : null
          ].filter((item) => item)}
        />
      </footer>
    </>
  );
}

BaseApp.propTypes = {
  /**
   * The app configuration
   */
  config: PropTypes.object.isRequired,
  /**
   * The static breadcrumb
   */
  staticBreadcrumb: PropTypes.object,
  /**
   * The authentication flag
   */
  hasAuthentication: PropTypes.bool,
  /**
   * The nested children
   */
  children: PropTypes.node
};

export default BaseApp;
